import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { useToast } from "@/shared/hooks/use-toast";
import { Receipt, Calendar, DollarSign, Store, CheckCircle, XCircle, CreditCard, Copy } from "lucide-react";

interface TransactionPlan {
  id?: string;
  active?: boolean;
  price?: number;
  store?: 'APPLE' | 'GOOGLE' | 'REVENUE_CAT';
}

interface Transaction {
  id?: string;
  amount?: number;
  currency?: string;
  status?: 'completed' | 'pending' | 'failed' | 'refunded';
  createdAt?: string | number | null;
  store?: 'APPLE' | 'GOOGLE' | 'REVENUE_CAT';
  productId?: string;
  receipt?: string;
  refunded?: boolean;
  plan?: TransactionPlan;
}

interface TransactionsFieldProps {
  transactions?: Transaction[];
}

const statusLabels = {
  completed: 'Concluída',
  pending: 'Pendente',
  failed: 'Falhou',
  refunded: 'Reembolsada',
};

const statusColors = {
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  failed: 'bg-red-500/20 text-red-400 border-red-500/30',
  refunded: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
};

const storeLabels = {
  REVENUE_CAT: 'Revenue Cat',
  APPLE: 'Apple Store',
  GOOGLE: 'Google Play',
};

const storeColors = {
  REVENUE_CAT: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  APPLE: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  GOOGLE: 'bg-green-500/20 text-green-400 border-green-500/30',
};

function formatDate(dateInput: Transaction['createdAt']): string {
  if (dateInput === null || dateInput === undefined || dateInput === '') {
    return 'Data indisponível';
  }

  const date = typeof dateInput === 'number' ? new Date(dateInput) : new Date(dateInput);

  if (Number.isNaN(date.getTime())) {
    return 'Data indisponível';
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatCurrency(amount?: number, currency: string = 'BRL'): string {
  if (amount === undefined || Number.isNaN(amount)) {
    return '—';
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency,
  }).format(amount);
}

export function TransactionsField({ transactions }: TransactionsFieldProps) {
  const { toast } = useToast();

  const handleCopy = async (value: string | undefined, label: string) => {
    if (!value) return;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(value);
        toast({
          title: "Copiado",
          description: `${label} copiado para a área de transferência.`,
        });
      } else {
        throw new Error("Clipboard API indisponível");
      }
    } catch {
      toast({
        title: "Erro ao copiar",
        description: "Não foi possível copiar o conteúdo.",
        variant: "destructive",
      });
    }
  };

  if (!transactions || transactions.length === 0) {
    return (
      <Card className="bg-[#1A1A1A] border-[#333333]">
        <CardHeader>
          <CardTitle className="text-white flex items-center text-base">
            <Receipt className="w-5 h-5 mr-2" />
            Transações
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-400">Nenhuma transação encontrada</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#1A1A1A] border-[#333333]">
      <CardHeader>
        <CardTitle className="text-white flex items-center justify-between text-base">
          <div className="flex items-center">
            <Receipt className="w-5 h-5 mr-2" />
            Transações
          </div>
          <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
            {transactions.length} {transactions.length === 1 ? 'transação' : 'transações'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-h-[400px] overflow-y-auto pr-4">
          <div className="space-y-4">
            {transactions.map((transaction, index) => (
              <Card key={transaction.id ?? transaction.plan?.id ?? index} className="bg-[#222222] border-[#333333]">
                <CardContent className="p-4 space-y-3">
                  {/* Cabeçalho da Transação */}
                  <div className="flex items-center justify-between pb-2 border-b border-[#333333]">
                    <div className="flex items-center space-x-2">
                      <CreditCard className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-semibold text-white">
                        Transação #{index + 1}
                      </span>
                    </div>
                    {transaction.refunded && (
                      <Badge variant="outline" className={statusColors.refunded}>
                        <XCircle className="w-3 h-3 mr-1" />
                        Reembolsada
                      </Badge>
                    )}
                    {!transaction.refunded && transaction.status && (
                      <Badge
                        variant="outline"
                        className={statusColors[transaction.status] || statusColors.completed}
                      >
                        {transaction.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                        {transaction.status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
                        {statusLabels[transaction.status] || transaction.status}
                      </Badge>
                    )}
                  </div>

                  {/* Valor */}
                  {transaction.amount !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400 flex items-center">
                        <DollarSign className="w-4 h-4 mr-1" />
                        Valor
                      </span>
                      <span className="text-sm font-semibold text-white">
                        {formatCurrency(transaction.amount, transaction.currency)}
                      </span>
                    </div>
                  )}

                  {/* Data */}
                  {transaction.createdAt && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400 flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        Data
                      </span>
                      <span className="text-sm text-white">
                        {formatDate(transaction.createdAt)}
                      </span>
                    </div>
                  )}

                  {/* Loja */}
                  {transaction.store && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400 flex items-center">
                        <Store className="w-4 h-4 mr-1" />
                        Loja
                      </span>
                      <Badge
                        variant="outline"
                        className={storeColors[transaction.store]}
                      >
                        {storeLabels[transaction.store]}
                      </Badge>
                    </div>
                  )}

                  {/* Plano associado (quando fornecido pela API) */}
                  {transaction.plan && (
                    <div className="rounded-lg border border-[#333333] bg-[#181818] p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-300 flex items-center">
                          <Store className="w-4 h-4 mr-1" />
                          Plano vinculado
                        </span>
                        <Badge
                          variant={transaction.plan.active ? "default" : "secondary"}
                          className={
                            transaction.plan.active
                              ? "bg-green-500/20 text-green-400 border-green-500/30"
                              : "bg-red-500/20 text-red-400 border-red-500/30"
                          }
                        >
                          {transaction.plan.active ? "Ativo" : "Inativo"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>ID</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono truncate max-w-[140px]" title={transaction.plan.id}>
                            {transaction.plan.id ?? "—"}
                          </span>
                          {transaction.plan.id && (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 text-gray-300 hover:text-white"
                              onClick={() => handleCopy(transaction.plan?.id, "ID do plano")}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>Preço</span>
                        <span className="font-semibold text-white">
                          {transaction.plan.price !== undefined ? formatCurrency(transaction.plan.price) : "—"}
                        </span>
                      </div>
                      {transaction.plan.store && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">Loja</span>
                          <Badge
                            variant="outline"
                            className={storeColors[transaction.plan.store]}
                          >
                            {storeLabels[transaction.plan.store]}
                          </Badge>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ID do Produto */}
                  {transaction.productId && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Produto</span>
                      <span className="text-xs text-gray-400 font-mono truncate max-w-[200px]" title={transaction.productId}>
                        {transaction.productId}
                      </span>
                    </div>
                  )}

                  {/* ID da Transação */}
                  {transaction.id && (
                    <div className="flex items-center justify-between pt-2 border-t border-[#333333] gap-2">
                      <span className="text-xs text-gray-500">ID</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 font-mono truncate max-w-[180px]" title={transaction.id}>
                          {transaction.id}
                        </span>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 text-gray-300 hover:text-white"
                          onClick={() => handleCopy(transaction.id, "ID da transação")}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Receipt (se houver) */}
                  {transaction.receipt && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Receipt</span>
                      <span className="text-xs text-gray-400">✓ Disponível</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
